import React from 'react';
import { Metro, State, Store } from 'clinics/clinics';
import { S31StateHero, useBackgroundOptions, type MilanBackgroundColors } from '@milanlaser/mlcomponents';
import { type PaddingSize, paddingMap } from '../../utils/builderPaddingOptions';
import { processBackgroundColor } from '../../utils/builder.utils';
import { getLocationContext } from '../../../../utils/general/getLocationContext';
import type { LocationBuilderState } from '../../../../utils/general/builder';
import { useBuilderImages } from '../../context/BuilderImageContext';
import { useOptimizedImage } from '../../../../hooks/useOptimizedImage';
import { createImageRenderer } from '../../../../utils/general/renderOptimizedImage';

type BuilderState<T> = {
    device: string;
    deviceSize: string;
    isBrowser: boolean;
    isServer: boolean;
} & T;

type BuilderProps<T> = {
    builderState: {
        state?: BuilderState<T>;
    };
};

type S31StateHeroSectionProps = Omit<
    React.ComponentProps<typeof S31StateHero>,
    'city' | 'buttonHref' | 'learnMoreHref'
> &
    BuilderProps<{
        metro: Metro;
        state: State;
        store: Store;
        localeName: string;
        clinicImages: string[];
        model: 'clinic-page' | 'state-page' | 'metro-page';
    }> & {
        desktopPaddingTop?: PaddingSize;
        desktopPaddingBottom?: PaddingSize;
        mobilePaddingTop?: PaddingSize;
        mobilePaddingBottom?: PaddingSize;
        backgroundColor?: MilanBackgroundColors;
    };

export function S31StateHeroSection(props: S31StateHeroSectionProps) {
    const {
        desktopPaddingTop = 'large',
        desktopPaddingBottom = 'large',
        mobilePaddingTop = 'small',
        mobilePaddingBottom = 'small',
        backgroundColor,
        imageSrc,
        imageAltText,
        ...restProps
    } = props;

    const { builderState } = restProps;
    const { getOptimizedImage } = useBuilderImages();

    const topPaddingClasses = `mcl-pt-${paddingMap[mobilePaddingTop]} lg:mcl-pt-${paddingMap[desktopPaddingTop]}`;
    const bottomPaddingClasses = `mcl-pb-${paddingMap[mobilePaddingBottom]} lg:mcl-pb-${paddingMap[desktopPaddingBottom]}`;
    const { backgroundStyles } = useBackgroundOptions({
        backgroundColor: processBackgroundColor({
            backgroundColor,
        }),
    });

    const location = getLocationContext(builderState as LocationBuilderState);

    if (!location) {
        return <h2 className="py-4">Oops! This component can only be used on state or metro pages.</h2>;
    }

    // Get stateAbbrev and headlinePrefix only for metro pages
    const model = builderState?.state?.model;
    const stateAbbrev = model === 'metro-page' ? builderState?.state?.metro?.stateAbbrev : undefined;
    const headlinePrefix = model === 'metro-page' ? 'Best Laser Hair Removal in ' : undefined;
    const introOverride = model === 'metro-page' ? builderState?.state?.metro?.metro_meta_paragraph : undefined;

    const optimizedImageInfo = imageSrc ? getOptimizedImage(imageSrc) : null;
    const { gatsbyImageData, altText } = useOptimizedImage(
        optimizedImageInfo?.nodeId || '',
        optimizedImageInfo?.altText || imageAltText || `${location} clinic interior`
    );

    const renderImage = createImageRenderer({
        optimizedImageData: gatsbyImageData,
        optimizedAlt: altText,
        shouldUseOptimized: () => !!optimizedImageInfo && !!imageSrc,
    });

    return (
        <section className={`${topPaddingClasses} ${bottomPaddingClasses} ${backgroundStyles}`}>
            <div className="container">
                <S31StateHero
                    {...restProps}
                    description={introOverride || restProps.description}
                    city={location}
                    stateAbbrev={stateAbbrev}
                    headlinePrefix={headlinePrefix}
                    buttonHref="#consult"
                    learnMoreHref="/what-is-laser-hair-removal"
                    imageSrc={imageSrc}
                    imageAltText={imageAltText}
                    renderImage={renderImage}
                />
            </div>
        </section>
    );
}
